import React, { useState, useEffect } from 'react';
import { Image, Plus, Edit2, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import api from '../services/api';

const Wallpapers = () => {
  const [wallpapers, setWallpapers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingWallpaper, setEditingWallpaper] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    thumbnailUrl: '',
    category_id: '',
    isActive: true,
    isFeatured: false,
  });

  useEffect(() => {
    fetchCategories();
    fetchWallpapers();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/wallpaper-categories');
      setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchWallpapers = async () => {
    try {
      const { data } = await api.get('/wallpapers');
      console.log(data);
      setWallpapers(data.data);
    } catch (error) {
      console.error('Error fetching wallpapers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log(formData);
  
      if (editingWallpaper) {
        await api.put(`/wallpapers/${editingWallpaper.id}`, formData);
      } else {
        await api.post('/wallpapers', formData);
      }
      fetchWallpapers();
      setShowDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error saving wallpaper:', error);
      alert(error.response?.data?.message || 'Error saving wallpaper');
    }
  };

  const toggleActive = async (id) => {
    try {
      await api.post(`/wallpapers/${id}/toggle`);
      fetchWallpapers();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const toggleFeatured = async (id) => {
    try {
      await api.post(`/wallpapers/${id}/feature`);
      fetchWallpapers();
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const deleteWallpaper = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wallpaper?')) return;
    try {
      await api.delete(`/wallpapers/${id}`);
      fetchWallpapers();
    } catch (error) {
      console.error('Error deleting wallpaper:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      imageUrl: '',
      thumbnailUrl: '',
      category_id: '',
      isActive: true,
      isFeatured: false,
    });
    setEditingWallpaper(null);
  };

  const openEditDialog = (wallpaper) => {
    setEditingWallpaper(wallpaper);
    setFormData({
      title: wallpaper.title,
      imageUrl: wallpaper.imageUrl,
      thumbnailUrl: wallpaper.thumbnailUrl || '',
      category_id: wallpaper.category_id,
      isActive: wallpaper.isActive,
      isFeatured: wallpaper.isFeatured,
    });
    setShowDialog(true);
  };

  // Filter wallpapers by category
  const filteredWallpapers = selectedCategory === 'all'
    ? wallpapers
    : wallpapers.filter(w => w.category_id === parseInt(selectedCategory));

  if (loading) return <div className="text-center py-20">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Wallpapers</h1>
          <p className="text-gray-400">Manage wallpapers for the app</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 px-6 py-3 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Add Wallpaper
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg whitespace-nowrap ${
            selectedCategory === 'all'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          All ({wallpapers.length})
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id.toString())}
            className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 ${
              selectedCategory === category.id.toString()
                ? 'text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            style={{
              backgroundColor: selectedCategory === category.id.toString() ? category.color : '',
            }}
          >
            <span>{category.icon}</span>
            <span>{category.name}</span>
            <span className="text-xs opacity-70">
              ({wallpapers.filter(w => w.category_id === category.id).length})
            </span>
          </button>
        ))}
      </div>

      {/* Wallpapers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredWallpapers.map((wallpaper) => (
          <div
            key={wallpaper.id}
            className="gradient-card border border-gray-800 rounded-xl overflow-hidden"
          >
            {/* Image */}
            <div className="h-48 bg-gray-900 flex items-center justify-center relative">
              {wallpaper.thumbnailUrl ? (
                <img 
                  src={wallpaper.thumbnailUrl} 
                  alt={wallpaper.title}
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              ) : (
                <Image size={48} className="text-gray-700" />
              )}
              {wallpaper.isFeatured && (
                <div className="absolute top-2 right-2 bg-gradient-to-r from-orange-500 to-red-500 px-2 py-1 rounded-lg flex items-center gap-1">
                  <Star size={12} />
                  <span className="text-xs font-bold">Featured</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-4 space-y-3">
              <div>
                <h3 className="font-bold text-white">{wallpaper.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg">{wallpaper.category_icon}</span>
                  <span className="text-sm text-gray-400">{wallpaper.category_name}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{wallpaper.downloads} downloads</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(wallpaper.id)}
                  className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 ${
                    wallpaper.isActive
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  }`}
                  title={wallpaper.isActive ? 'Active' : 'Inactive'}
                >
                  {wallpaper.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => toggleFeatured(wallpaper.id)}
                  className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center ${
                    wallpaper.isFeatured
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                  title="Toggle Featured"
                >
                  <Star size={16} />
                </button>
                <button
                  onClick={() => openEditDialog(wallpaper)}
                  className="flex-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => deleteWallpaper(wallpaper.id)}
                  className="flex-1 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredWallpapers.length === 0 && (
        <div className="text-center py-20">
          <Image size={64} className="mx-auto text-gray-700 mb-4" />
          <p className="text-gray-400">No wallpapers in this category</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="gradient-card border border-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingWallpaper ? 'Edit Wallpaper' : 'Add Wallpaper'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Category <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <p className="text-sm text-orange-400 mt-2">
                    ⚠️ No categories found. Please create categories first.
                  </p>
                )}
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>

              {/* Thumbnail URL */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Thumbnail URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.thumbnailUrl}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="https://example.com/thumbnail.jpg"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Featured</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 rounded-lg"
                  disabled={!formData.category_id}
                >
                  {editingWallpaper ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallpapers;